import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { format } from 'date-fns';

import {
	BlockGridItemPreviewObject,
	CreateBlockRequest,
	getQueryError,
	handleApiResponse,
	ShortOption,
	useAddBlockMutation,
	useDeleteBlockGridItemMutation,
	useDeleteBlockPatternMutation,
	useEndBlockPatternMutation,
	useGetBlockGridItemPreviewQuery,
	useGetBlockPatternMutation,
	useGetBlockPatternPreviewQuery,
	useGetBlockServiceLineQuery,
	useSystem,
} from 'store';
import { useFilters, useToast } from 'context';
import { Button, Datepicker, Select, TextField, ToggleGroup } from 'components';
import { UserRole } from 'models';
import { increaseDateByYear, inputFieldRegex, userIsInternalAdmin } from 'utils';

import { BlockPatternDetailsPOSTTemp, Step1CachePropsTemp } from './BlockPatternFlowTemp';
import { dowConst } from './BlockPatternFlow';
import { BlockPreviewHeader } from '../BlockScorecard';

function BlockGridItemPreviewHeader({
	data,
	selectMode,
	onSelect,
	defaultSelected,
}: {
	data: BlockGridItemPreviewObject[];
	selectMode?: boolean;
	onSelect?: (dow: string, i: number, grid_item_id: number) => void;
	defaultSelected?: number;
}) {
	const [selectedIndex, setSelectedIndex] = useState<number | null>(defaultSelected ?? null);

	useEffect(() => {
		setSelectedIndex(null);
	}, []);

	return (
		<>
			{!selectMode && (
				<div className='flex text-p3'>
					{data.map((d, i) => (
						<div className='flex flex-col gap-2 pr-12' key={i}>
							<div className='flex justify-between px-2'>
								<p className='text-right font-semibold uppercase'>{d.day_of_week}</p>
							</div>
							<p className='text-p3 bg-blue-50 w-fit pt-1 pb-0.5 px-2 font-semibold rounded-2xl whitespace-nowrap self-center'>
								{d.start_time}-{d.end_time}
							</p>
							<p className='px-2'>({d.allocated_minutes} min)</p>
							<p className='pl-1'>Week {String(d.week)}</p>
						</div>
					))}
				</div>
			)}

			{!selectMode && data.length === 0 && (
				<div className='flex flex-col gap-2 pr-12 opacity-0'>
					<div className='flex justify-between px-2'>
						<p className='text-right font-semibold uppercase'>None</p>
						<p className='pl-1'>None</p>
					</div>
					<p className='text-p3 bg-blue-50 w-fit pt-1 pb-0.5 px-2 font-semibold rounded-2xl whitespace-nowrap self-center'>
						None-None
					</p>
					<p className='px-2'>None</p>
				</div>
			)}

			{selectMode && onSelect && (
				<div className='flex text-p3'>
					{data.map((d, i) => (
						<div
							className={`flex ${
								selectedIndex === i ? 'border-blue-500' : 'border-white'
							} flex-col gap-2 mb-2 p-5 rounded-md cursor-pointer border hover:border hover:border-blue-500 text-center`}
							key={i}
							onClick={() => {
								setSelectedIndex(i);
								onSelect(d.day_of_week, i, d.grid_item_id);
							}}
						>
							<div className='text-center'>
								<p className='font-semibold uppercase'>{d.day_of_week}</p>
							</div>
							<p className='text-p3 bg-blue-50 w-fit pt-1 pb-0.5 px-2 font-semibold rounded-2xl whitespace-nowrap self-center'>
								{d.start_time}-{d.end_time}
							</p>
							<p className='justify-between'>({d.allocated_minutes} min)</p>
							<p className=''>Week {String(d.week)}</p>
						</div>
					))}
				</div>
			)}
		</>
	);
}

function BlockPatternFlowChoiceTemp(props: {
	setFlowMode: (mode: 'Add' | 'Edit' | 'Delete') => void;
	setShowActiveOrExpiredPatterns: (option: 'active' | 'expired') => void;
	closeModule: () => void;
	wipeCache: () => void;
	submit: (data: Step1CachePropsTemp) => void;
	setSelectedBlockPattern: (data: BlockPatternDetailsPOSTTemp) => void;
	facility_id: number | null;
	cachedData?: {
		mode: 'Add' | 'Edit' | 'Delete' | 'End';
		block_option: ShortOption | null;
		dow_selected: number | null;
		dow_selected_index?: number | null;
		block_grid_metadata_present?: boolean;
		room_space: string;
		block_name: string;
		block_grid_state_id: number;
		block_grid_item_id: number;
	};
	healthsystem_id: number | null;
}) {
	const [flowMode, setFlowMode] = useState<'Add' | 'Edit' | 'Delete' | 'End'>(
		props.cachedData ? props.cachedData.mode : 'Add'
	);
	const [showPastPatterns, setShowPastPatterns] = useState('active');
	const [gettingPattern, setGettingPattern] = useState<boolean>(false);
	const { createToast } = useToast();
	const { data: facility_data } = useSystem();
	const isAdmin = userIsInternalAdmin(facility_data?.user);

	const [blockSelected, setBlock] = useState<ShortOption | null>(
		props.cachedData ? props.cachedData.block_option : null
	);
	const [dowSelected, setDowSelected] = useState<number | null>(props.cachedData ? props.cachedData.dow_selected : null);
	const [dowSelectedIndex, setDowSelectedIndex] = useState<number | null>(
		props.cachedData?.dow_selected_index !== undefined ? props.cachedData?.dow_selected_index : null
	);
	const [getBlockPattern] = useGetBlockPatternMutation();
	const [deleteBlockPattern] = useDeleteBlockPatternMutation();
	const [deleteBlockGridItem] = useDeleteBlockGridItemMutation();
	const [endBlockPattern] = useEndBlockPatternMutation();
	const [selectedPattern, setSelectedPattern] = useState<number | null>(null); // stores block pattern id
	const [selectedGridItem, setSelectedGridItem] = useState<number | null>(null); // block grid item id

	// Filters
	const { blockName, date } = useFilters();
	interface Option {
		value: number;
		label: string;
	}

	const blockNameOptions: Option[] = blockName?.all
		? (
				[
					// Explicitly type the array literal
					{ value: -1, label: 'Open Time' },
					{ value: -2, label: 'Closed Time' },
				] as Option[]
		  ).concat(
				// Ensure this part is Option[]
				blockName.all.map(
					({ id, name }): Option => ({
						// Explicitly type the mapped object
						value: id, // Ensure id is number or compatible with Option.value
						label: name, // Ensure name is string or compatible with Option.label
					})
				)
		  )
		: ([
				// Explicitly type the array literal for the false case
				{ value: -1, label: 'Open Time' },
				{ value: -2, label: 'Closed Time' },
		  ] as Option[]); // Ensure this part is Option[]

	const { data: blockPatternPreview, isFetching } = useGetBlockPatternPreviewQuery(
		{
			selected_block: blockSelected, // contains block_id
			facility_id: props.facility_id,
			healthsystem_id: props.healthsystem_id,
			show_previous_only: showPastPatterns === 'expired',
		},
		{
			skip: true,
		}
	);

	const { data: blockGridItemPreview, isFetching: blockGridIsFetching } = useGetBlockGridItemPreviewQuery(
		{
			room_space_id: props.cachedData?.room_space ?? null,
			facility_id: props.facility_id ?? 1,
			healthsystem_id: props.healthsystem_id ?? 1,
			block_name: blockSelected?.name ?? null,
			day_of_week: props.cachedData?.dow_selected ?? 2,
			block_grid_item_id: props.cachedData?.block_grid_item_id ?? -1,
		},
		{
			skip: !blockSelected,
		}
	);

	// options for add pattern toggle
	const addablePatternDoW = [
		{ label: 'Monday', value: '2', disabled: false },
		{ label: 'Tuesday', value: '3', disabled: false },
		{ label: 'Wednesday', value: '4', disabled: false },
		{ label: 'Thursday', value: '5', disabled: false },
		{ label: 'Friday', value: '6', disabled: false },
	];
	const existingBlockPatterns = blockPatternPreview?.data ?? [];

	const existingBlockGridItems = blockGridItemPreview?.data ?? [];

	// get service line
	const { data: serviceline_data } = useGetBlockServiceLineQuery(
		{
			healthsystem_id: props.healthsystem_id,
			selected_block: blockSelected ?? undefined,
		},
		{
			skip: !blockSelected,
		}
	);
	const [endDateLimit, setEndDateLimit] = useState<Date>(date.selected);
	const getCurrentBlockPattern = async (block_pattern_id: number) => {
		setGettingPattern(true);
		const response = await getBlockPattern({
			block_pattern_id: block_pattern_id,
		});

		handleApiResponse(response, {
			success: (payload) => {
				props.setSelectedBlockPattern(payload);
				setGettingPattern(false);
				setEndDateLimit(new Date(payload.effective_date));
			},
			error: (payload) => {
				setGettingPattern(false);
			},
		});
	};
	const [endDate, setEndDate] = useState(new Date());
	const endSelectedBlockPattern = async () => {
		setGettingPattern(true);

		const response = await endBlockPattern({
			block_pattern_id: selectedPattern,
			effective_date: format(endDate, 'M/d/yyyy'),
		});

		handleApiResponse(response, {
			success: (payload) => {
				createToast({
					title: 'Block Pattern Terminated',
					variant: 'success',
				});

				// last thing to execute
				setTimeout(() => {
					props.closeModule();
				}, 0);
			},
			error: (payload) => {
				createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	const deleteSelectedBlockPattern = async () => {
		setGettingPattern(true);

		const response = await deleteBlockPattern({
			block_pattern_id: selectedPattern,
		});

		handleApiResponse(response, {
			success: (payload) => {
				createToast({
					title: 'Block Pattern Deleted',
					variant: 'success',
				});

				// last thing to execute
				setTimeout(() => {
					props.closeModule();
				}, 0);
			},
			error: (payload) => {
				createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	const deleteSelectedBlockGridItem = async () => {
		setGettingPattern(true);

		const response = await deleteBlockGridItem({
			block_grid_item_id: selectedGridItem,
		});

		handleApiResponse(response, {
			success: (payload) => {
				createToast({
					title: 'Block Grid Item Deleted',
					variant: 'success',
				});

				// last thing to execute
				setTimeout(() => {
					props.closeModule();
				}, 0);
			},
			error: (payload) => {
				createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};

	const [blockNameInput, setBlockNameInput] = useState<string>('');
	const [errorMessage, setErrorMessage] = useState<undefined | string>(undefined);
	const addBlockSubmit = async (data: CreateBlockRequest) => {
		setStatus('loading');
		//setModuleTitle('Loading...');

		const response = await addBlock(data);

		handleApiResponse(response, {
			success: () => {
				setStatus('success');
				//setModuleTitle('Block Created');
				createToast({
					title: 'Block created successfully',
					variant: 'success',
				});
				restoreDefaultsandClose();
			},
			error: (payload) => {
				setStatus('failure');
				//setModuleTitle('Failed to create block');
				createToast({
					title: getQueryError(payload),
					variant: 'error',
				});
			},
		});
	};
	const [status, setStatus] = useState<null | 'loading' | 'success' | 'failure'>(null);

	const restoreDefaultsandClose = () => {
		//setModuleTitle('Block Module');
		setStatus(null);
	};
	const [addBlock] = useAddBlockMutation();
	const [createNewBlock, setCreateNewBlock] = useState(false);
	return (
		<>
			<div className='flex my-8 items-center'>
				<div className='w-96 pr-12'>
					<p className='font-bold mb-2 uppercase text-p2'>Action</p>
					<p className='text-gray-400 italic text-p3'>
						Use this option to determine which action you would like to take to edit the block schedule.
					</p>
				</div>
				<div className='min-w-[550px]'>
					<ToggleGroup
						label='Add or Edit Block'
						hideLabel
						defaultValue={flowMode}
						disabled={props.cachedData?.block_grid_metadata_present}
						options={[
							{ label: 'Add Block Pattern', value: 'Add' },
							{ label: 'Delete Block Pattern', value: 'Delete' },
						]}
						onValueChange={(e: 'Add' | 'Edit' | 'Delete') => {
							setFlowMode(e);
							setDowSelected(null);
							setBlock(null);
							setDowSelectedIndex(null);
							props.wipeCache();
							props.setFlowMode(e);
						}}
					/>
				</div>
			</div>
			<div className='flex items-center mb-4 mt-6'>
				{/* if block_grid_metadata provided, give the user the choice to use existing block name or create a new one in this flow */}
				<>
					<div>
						{props.cachedData?.block_grid_metadata_present && (
							<>
								<Select
									label={`Create new block name or use an existing name`}
									options={[
										{ value: 0, label: 'New' },
										{ value: 1, label: 'Existing' },
									]}
									onChange={(selection: any) => {
										if (!selection) return;
										if (selection.label === 'New') {
											setCreateNewBlock(true);
										} else {
											setCreateNewBlock(false);
										}
									}}
									sizeX='lg'
									sizeY='sm'
									value={
										createNewBlock
											? { value: createNewBlock === true ? 0 : 1, label: createNewBlock === true ? 'New' : 'Existing' }
											: { value: 1, label: 'Existing' }
									}
								/>
								<div className='pb-3'></div>
							</>
						)}
						{createNewBlock ? (
							<TextField
								label='New Block Name'
								value={blockNameInput}
								placeholder='Block Name'
								onChange={(e) => {
									if (inputFieldRegex.test(e.target.value) && e.target.value.length < 254) {
										setErrorMessage(undefined);
									} else {
										setErrorMessage('Please enter a valid block name');
									}
									setBlockNameInput(e.target.value);
								}}
								errorMessage={errorMessage}
							></TextField>
						) : (
							<Select
								label={`Existing Block Name`}
								options={blockNameOptions}
								onChange={(selection) => {
									if (!selection) return;
									const selectedBlockName = { id: selection.value, name: selection.label };
									// setDowSelected(null);
									setBlock(selectedBlockName);
								}}
								sizeX='lg'
								sizeY='sm'
								value={blockSelected ? { value: blockSelected.id, label: blockSelected.name } : null}
								isWorking={blockName.isFetching || isFetching}
							/>
						)}{' '}
					</div>
				</>
				<div className='w-2/3 ml-7'>
					{flowMode === 'Add' &&
						blockSelected &&
						!isFetching &&
						(addablePatternDoW.length > 0 ? (
							<ToggleGroup
								label='Day of Week of block pattern'
								defaultValue={`${dowSelected}`}
								options={addablePatternDoW}
								onValueChange={(e: string) => {
									setDowSelected(parseInt(e));
								}}
							/>
						) : (
							<p className='mt-7 text-gray-300 text-i3 w-3/4'>
								Patterns already exist for all days of week. Edit block patterns by clicking the Edit Block Pattern button.
							</p>
						))}
				</div>
			</div>
			<div className='flex my-6 items-center'>
				{(flowMode === 'Edit' || flowMode === 'Delete' || flowMode === 'End') && blockSelected && (
					<>
						<div className='w-96 pr-12'>
							<p className='font-bold mb-2 uppercase text-p2 whitespace-nowrap'>{`Select ${
								showPastPatterns === 'expired' ? 'Expired' : 'Existing'
							} Block Pattern`}</p>
							<p className='text-gray-400 italic text-p3'>
								These are the existing block patterns with this block name. You cannot add a block day on a day where a block
								allocation already exists.
							</p>
						</div>
						<div className='overflow-x-auto mr-4'>
							{!isFetching &&
								!props.cachedData?.block_grid_metadata_present &&
								(existingBlockPatterns.length > 0 ? (
									<BlockPreviewHeader
										selectMode={true}
										onSelect={(day, i, block_pattern_id, start_date) => {
											setDowSelectedIndex(i);
											setDowSelected(dowConst.indexOf(day) + 1);
											getCurrentBlockPattern(block_pattern_id);
											setSelectedPattern(block_pattern_id);
											setEndDate(increaseDateByYear(new Date(start_date), 1));
										}}
										data={existingBlockPatterns}
										defaultSelected={dowSelectedIndex !== null ? dowSelectedIndex : undefined}
									/>
								) : (
									<p className='text-gray-300 text-i3 w-3/4'>
										No block patterns exist for this block. Add block patterns by clicking the Add Block Pattern button.
									</p>
								))}
							{!isFetching &&
								props.cachedData?.block_grid_metadata_present &&
								(existingBlockGridItems.length > 0 ? (
									<BlockGridItemPreviewHeader
										selectMode={true}
										onSelect={(day, i, block_grid_item_id) => {
											setDowSelectedIndex(i);
											setDowSelected(dowConst.indexOf(day) + 1);
											//getCurrentBlockPattern(block_grid_item_id);
											setSelectedGridItem(block_grid_item_id);
										}}
										data={existingBlockGridItems}
										defaultSelected={dowSelectedIndex !== null ? dowSelectedIndex : undefined}
									/>
								) : (
									<p className='text-gray-300 text-i3 w-3/4'>
										No block patterns exist for this block. Add block patterns by clicking the Add Block Pattern button.
									</p>
								))}
							{isFetching && <div className='animate-pulse bg-gray-50 rounded-md h-[117px] w-96'></div>}
						</div>
					</>
				)}
			</div>
			{isAdmin && flowMode === 'Edit' && (
				<div>
					<ToggleGroup
						label='Type of Block Pattern'
						className='w-1/6'
						options={[
							{ label: 'Active', value: 'active' },
							{ label: 'Expired', value: 'expired' },
						]}
						value={showPastPatterns}
						onValueChange={(option: 'active' | 'expired') => {
							setShowPastPatterns(option);
							props.setShowActiveOrExpiredPatterns(option);
						}}
					/>
				</div>
			)}

			<div className='flex my-6 items-center'>
				{flowMode === 'End' &&
					blockSelected &&
					!isFetching &&
					(addablePatternDoW.length > 0 ? (
						<div className='flex flex-col text-left'>
							<p className='text-p3 text-gray-700 tracking-wider uppercase mb-2 font-secondary'>Termination Date</p>
							<div className={classNames('min-w-[250px] z-[300]')}>
								<Datepicker
									type='date'
									popoverContentProps={{ side: 'left' }}
									onApply={(newDate) => {
										setEndDate(newDate);
									}}
									selected={endDate}
									customLimit={{ minLimit: endDateLimit, maxLimit: false }}
								>
									<div
										className={classNames(
											'flex justify-center border rounded-sm border-gray-300 p-2 text-p3 w-48',
											!selectedPattern ? 'opacity-25 !cursor-not-allowed' : '!cursor-pointer'
										)}
										onClick={(e) => !selectedPattern && e.preventDefault()}
									>
										<div className='rounded text-white bg-blue-500 py-1 px-6'>{format(endDate, 'M/d/yyyy')}</div>
									</div>
								</Datepicker>
							</div>
						</div>
					) : (
						<p className='mt-7 text-gray-300 text-i3 w-3/4'>
							Patterns already exist for all days of week. Edit block patterns by clicking the Edit Block Pattern button.
						</p>
					))}
			</div>
			<div className='absolute bottom-8 right-11'>
				<div className='w-full flex justify-end mt-20'>
					<Button
						sizeX='md'
						sizeY='sm'
						variant='secondary'
						className='mr-4'
						onClick={() => {
							props.closeModule();
						}}
					>
						Cancel
					</Button>
					<Button
						sizeX='md'
						sizeY='sm'
						disabled={
							!(blockSelected && dowSelected !== null) ||
							isFetching ||
							((flowMode === 'Edit' || flowMode === 'End') && gettingPattern)
						}
						isWorking={(flowMode === 'Edit' || flowMode === 'End') && gettingPattern}
						onClick={() => {
							if (flowMode !== 'End' && flowMode !== 'Delete') {
								props.submit({
									block_option: blockSelected,
									dow_selected: dowSelected,
									dow_selected_index: dowSelectedIndex,
									patterns:
										blockGridItemPreview?.data.map((e) => ({
											day_of_week: e.day_of_week,
											week_of_month: [parseInt(e.week)],
											range: `${e.start_time} - ${e.end_time}`,
											total_min: e.allocated_minutes,
										})) ?? [],
									flow: flowMode,
									service_line_id: serviceline_data?.serviceline_id,
									room_space: props.cachedData?.room_space ?? '',
									block_name: blockNameInput !== '' ? blockNameInput : blockSelected?.name ? blockSelected?.name : '',
									block_grid_state_id: props.cachedData?.block_grid_state_id ?? -1,
								});
							} else {
								if (flowMode === 'End') {
									// we do this so all the states are settled before sumbit
									setTimeout(() => {
										endSelectedBlockPattern();
									}, 0);
								} else {
									if (props.cachedData?.block_grid_metadata_present) {
										// we do this so all the states are settled before sumbit
										setTimeout(() => {
											deleteSelectedBlockGridItem();
										}, 0);
									} else {
										// we do this so all the states are settled before sumbit
										setTimeout(() => {
											deleteSelectedBlockPattern();
										}, 0);
									}
								}
							}
						}}
					>
						{flowMode !== 'End' && flowMode !== 'Delete' ? 'Next' : 'Submit'}
					</Button>
				</div>
			</div>
		</>
	);
}

export default BlockPatternFlowChoiceTemp;
